import { useEffect, useState } from 'react';
import { giftCardsAPI } from '@matix/api-client';
import type { GiftCardOnReceipt } from '@matix/types';
import { useGiftCards } from '../../store/giftCards';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';

/** The card the cashier chose to refund onto. */
export interface GiftCardRefundChoice {
  cardNumber: string;
  masked: string;
  refundable: number;
}

/**
 * Refund back onto the gift card that paid for the original invoice.
 *
 * This is the honest answer to "how do you refund an order somebody paid for
 * with a gift card": the money never came from a drawer, so it should not go
 * back into one. The card that funded the invoice is credited instead.
 *
 * Three things make it safe rather than a way to mint money:
 *
 *   1. It only appears for a return against a KNOWN invoice
 *      (`parentReceiptId`). Without one there is nothing to cap a credit
 *      against, and the server refuses gift-card refunds outright.
 *   2. The cashier picks from the cards that actually paid, read from
 *      `GET /gift-cards/for-receipt/:id` — no card number is typed.
 *   3. Only cards whose remaining refundable amount COVERS the whole return are
 *      offered. The refund leg settles on a single method, so a partial cover
 *      would need a split refund the receipt cannot express; the cashier uses an
 *      ordinary tender in that case.
 *
 * The server re-derives the cap under lock anyway, so a card that another
 * terminal refunded in the meantime fails the charge rather than over-crediting.
 */
export function GiftCardRefundPanel({
  parentReceiptId,
  returnTotal,
  selected,
  onSelect,
  disabled = false,
}: {
  parentReceiptId: number | null;
  returnTotal: number;
  selected: GiftCardRefundChoice | null;
  onSelect: (choice: GiftCardRefundChoice | null) => void;
  disabled?: boolean;
}): JSX.Element | null {
  const { t: tr } = useLang();
  const config = useGiftCards((s) => s.config);
  const [cards, setCards] = useState<GiftCardOnReceipt[]>([]);

  const enabled =
    config.enabled && config.allow_refund_to_card && config.payment_type_code != null;

  useEffect(() => {
    if (!enabled || parentReceiptId == null) {
      setCards([]);
      return;
    }
    let cancelled = false;
    void giftCardsAPI
      .forReceipt(parentReceiptId)
      .then((rows) => { if (!cancelled) setCards(rows); })
      // A tenant without the schema, or a receipt with no gift-card funding,
      // simply has nothing to offer here. The ordinary refund methods stay.
      .catch(() => { if (!cancelled) setCards([]); });
    return () => { cancelled = true; };
  }, [enabled, parentReceiptId]);

  if (!enabled || cards.length === 0) return null;

  const eligible = cards.filter((c) => c.refundable >= returnTotal - 1e-9);
  if (eligible.length === 0) return null;

  return (
    <div className="pay-giftcard-refund">
      <div className="pay-section-title" style={{ marginTop: 14 }}>
        {tr({ en: 'Refund onto the gift card', ar: 'الاسترداد إلى بطاقة الهدايا' })}
      </div>
      <div className="pay-methods">
        {eligible.map((c) => {
          const isOn = selected?.cardNumber === c.card_number;
          return (
            <button
              key={c.card_number}
              type="button"
              className={`pay-method ${isOn ? 'pay-method-used' : ''}`}
              onClick={() =>
                onSelect(
                  isOn
                    ? null
                    : {
                        cardNumber: c.card_number,
                        masked: c.card_number_masked,
                        refundable: c.refundable,
                      },
                )
              }
              disabled={disabled}
              title={tr({
                en: `This card paid ${fmt(c.paid)} on the original invoice`,
                ar: `دفعت هذه البطاقة ${fmt(c.paid)} على الفاتورة الأصلية`,
              })}
            >
              <span className="pay-method-name ops-mono">{c.card_number_masked}</span>
              <span className="pay-method-name-ar">
                {tr({ en: 'up to', ar: 'حتى' })} {fmt(c.refundable)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
